import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import 'react-tabs/style/react-tabs.css';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Backdrop, CircularProgress, Collapse, Alert } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import DoneIcon from '@mui/icons-material/Done';
import { getBalance, claim } from '../../helpers';  
import { useChainModal } from '@rainbow-me/rainbowkit';
import { useChainId} from 'wagmi';

const ClaimForm = ({ address }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar(); 
  const { openChainModal } = useChainModal();
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [balanceInfo, setBalanceInfo] = useState({});
  const [isCopied, setIsCopied] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [showSwapHistory, setShowSwapHistory] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const chainId = useChainId();

  const handleGetBalance = async () => {
    setIsLoading(true);
    if (address) {
      if (chainId !== 8453) {
        setNetworkError(true);
        setIsLoading(false);
        openChainModal(8453);
        return;
      } else {
        setNetworkError(false);
      }
      const balances = await getBalance(address);
      if (balances.success) {
        setBalanceInfo(balances);
      } else {
        snackMessage(balances.message, 'warning');
        setIsLoading(false);
      }
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleClaim = async () => {
    if (address) {
      if (parseFloat(balanceInfo.vestedAmount) > 0) {
        setIsSending(true);
        const response = await claim();
        snackMessage(response.message, response.success ? 'success' : 'warning');
        setIsSending(false);
        setTimeout(() => {
          handleGetBalance();
        }, 10000);
      } else {
        snackMessage('There is no vested amount!', 'warning');
      }
    }
  };

  const handleCopy = (str) => {
    navigator.clipboard.writeText(str);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const snackMessage = (message, variant) => {
    enqueueSnackbar(message, { variant });
    setTimeout(() => {
      closeSnackbar();
    }, 5000);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat().format(number);
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString('en-GB', {
      hour12: false,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRemainingTime = (timestamp) => {
    const now = Math.floor(Date.now() / 1000);
    const remaining = timestamp - now;
    const days = Math.floor(remaining / (3600 * 24));
    const hours = Math.floor((remaining % (3600 * 24)) / 3600);
    if (days > 0) {
      return `${days} ${days === 1 ? 'Day' : 'Days'}`;
    }
    return `${hours} ${hours === 1 ? 'Hour' : 'Hours'}`;
  };

  useEffect(() => {
    handleGetBalance();
    const isDark = document.querySelector('body[class="is_dark"]');
    setIsDark(isDark ? true : false);
  }, [chainId]);

  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        const isDark = document.querySelector('body[class="is_dark"]');
        setIsDark(isDark ? true : false);
      }, 250);
    };
    document.addEventListener('mouseup', handler);

    return () => {
      document.removeEventListener('mouseup', handler);
    };
  });

  const toggleSwapHistory = () => {
    setShowSwapHistory(!showSwapHistory);
  };

  return (
    <>
      <div className="main payment">
        <div style={{ display: 'flex' }}>
          <h6 className="title" style={{ marginTop: 8, color: "#119f24" }}>Account Balance</h6>
          <IconButton onClick={handleGetBalance} style={{ color: isDark ? 'white' : '' }}>
            <CachedIcon />
          </IconButton>
        </div>

        {balanceInfo.swapHistory?.length === 0 && !networkError && (
          <Alert severity="info" style={{ marginTop: 20 }}>
            There are no swap transactions found or bridging to Base is taking 24 hours.
          </Alert>
        )}

        {networkError && (
          <Alert severity="error" style={{ marginTop: 20 }}>
            Claims are only available on the Base network. Please switch your network to Base.
          </Alert>
        )}

        <ul className="status" style={{ marginTop: 20 }}>
          <li>
            <p className="desc">Account Address</p>
            <p className="text">
              {address ? `${address.slice(0, 4)}....${address.slice(-5)}` : '.....'}
              {isCopied === false ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => {
                    handleCopy(address);
                  }}
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9 20C11.7614 20 14 17.7614 14 15C14 12.2386 11.7614 10 9 10C6.23858 10 4 12.2386 4 15C4 17.7614 6.23858 20 9 20ZM9 22C12.866 22 16 18.866 16 15C16 11.134 12.866 8 9 8C5.13401 8 2 11.134 2 15C2 18.866 5.13401 22 9 22Z"
                    fill="#777E90"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.0001 4C13.5193 4 12.1899 4.6424 11.273 5.66691C10.9047 6.07844 10.2725 6.11346 9.86093 5.74513C9.4494 5.37681 9.41438 4.74461 9.78271 4.33309C11.063 2.9026 12.9268 2 15.0001 2C18.866 2 22.0001 5.13401 22.0001 9C22.0001 11.0733 21.0975 12.937 19.667 14.2173C19.2554 14.5857 18.6232 14.5507 18.2549 14.1391C17.8866 13.7276 17.9216 13.0954 18.3331 12.7271C19.3577 11.8101 20.0001 10.4807 20.0001 9C20.0001 6.23858 17.7615 4 15.0001 4Z"
                    fill="#777E90"
                  />
                </svg>
              ) : (
                <DoneIcon />
              )}
            </p>
          </li>
          <li>
            <p className="desc">Vested Amount</p>
            <p className="text" style={{ color: '#119f24' }}>{balanceInfo.vestedAmount ? formatNumber(parseFloat(balanceInfo.vestedAmount).toFixed(6)) : '.....'} OSCAR</p>
          </li>
          <li>
            <p className="desc">Total Balance</p>
            <p className="text">{balanceInfo.balance ? formatNumber(balanceInfo.balance) : '.....'} OSCAR</p>
          </li>
          <li>
            <p className="desc">Claimed Amount</p>
            <p className="text">{balanceInfo.claimedAmount ? formatNumber(balanceInfo.claimedAmount) : '.....'} OSCAR</p>
          </li>
          <li>
            <p className="desc">Swap History</p>
            <p className="text" onClick={toggleSwapHistory} style={{ cursor: 'pointer', color: '#119f24' }}>View</p>
          </li>
        </ul>



        <Collapse in={showSwapHistory}>
          <TableContainer component={Paper} style={{ marginTop: 20, backgroundColor: '#f5f5f5' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>GraveYard</TableCell>
                  <TableCell>GraveYard Amount</TableCell>
                  <TableCell>OSCAR Amount</TableCell>
                  <TableCell>Distributed Amount</TableCell>
                  <TableCell>Remaining Balance</TableCell>
                  <TableCell>Vesting Period</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balanceInfo.swapHistory?.filter(swap => swap.isActive || swap.hopeInitialBalance !== swap.distributedAmount).map((swap, index) => (
                  <TableRow key={index}>
                    <TableCell>{swap.isActive ? 'Active' : 'Finished'}</TableCell>
                    <TableCell>
                      <a href={`${swap.token.explorer}address/${swap.variant}`} target="_blank" rel="noopener noreferrer">
                        <img src={swap.token.icon} alt={swap.token.symbol} style={{ width: 24, height: 24, marginRight: 8 }} />
                        {swap.token.symbol}
                      </a>
                    </TableCell>
                    <TableCell>{formatNumber(swap.variantAmount)}</TableCell>
                    <TableCell>{formatNumber(swap.hopeInitialBalance)}</TableCell>
                    <TableCell>{formatNumber(swap.distributedAmount)}</TableCell>
                    <TableCell>{formatNumber(swap.hopeInitialBalance - swap.distributedAmount)}</TableCell>
                    <TableCell>{swap.vestingSchedule}</TableCell>
                    <TableCell>{formatDateTime(swap.vestingStartTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>

        <div className="group-button" style={{ marginTop: 20 }}>
          <Button
            variant="contained"
            color="primary"
            style={{ borderRadius: 20, marginTop: 25, backgroundColor: "#119f24", width: 400 }}
            onClick={handleClaim}
            disabled={networkError} 
          >
            {isSending ? 'Confirming...' : `Claim ${balanceInfo.vestedAmount ? parseFloat(balanceInfo.vestedAmount).toFixed(2) : '0.00'} OSCAR`}
          </Button>
        </div>

        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </>
  );
};

export default ClaimForm;
